import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, Timestamp, query, orderBy } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from '../App';
import { Plus, Search, Trash2, Edit2, X, Loader2, Building2, DollarSign, Wallet } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Account } from '../types';
import { toast } from 'sonner';
import ConfirmationModal from '../components/ConfirmationModal';

const TANZANIAN_BANKS = [
  "CRDB Bank PLC",
  "NMB Bank PLC",
  "National Bank of Commerce (NBC)",
  "Standard Chartered Bank (T) Ltd",
  "Stanbic Bank (T) Ltd",
  "Diamond Trust Bank (DTB)",
  "Equity Bank (T) Ltd",
  "Absa Bank (T) Ltd",
  "KCB Bank (T) Ltd",
  "Exim Bank (T) Ltd",
  "Azania Bank Ltd",
  "I&M Bank (T) Ltd",
  "NCBA Bank (T) Ltd",
  "People's Bank of Zanzibar (PBZ)",
  "TIB Development Bank Ltd",
  "Bank of Africa (BOA)",
  "United Bank for Africa (UBA)",
  "DCB Commercial Bank PLC",
  "Maendeleo Bank PLC",
  "AccessBank Tanzania Ltd",
  "Akiba Commercial Bank PLC",
  "Amana Bank Ltd",
  "Mkombozi Commercial Bank PLC",
  "Mwanga Hakika Bank",
  "Tanzania Commercial Bank (TCB)"
];

const AccountsPage: React.FC = () => {
  const { isAdmin, isManager, isAccountant } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [formData, setFormData] = useState({ 
    name: '', 
    type: 'bank' as Account['type'], 
    balance: 0, 
    currency: 'TZS',
    accountName: '',
    accountNumber: '',
    bankName: '',
    branchName: '',
    branchCode: '',
    sortCode: '',
    swiftCode: ''
  });

  useEffect(() => {
    const q = query(collection(db, 'accounts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Account)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'accounts');
    });
    return unsubscribe;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin && !isManager && !isAccountant) {
      toast.error('You do not have permission to save accounts');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const dataToSave = {
        ...formData,
        balance: Number(formData.balance),
        updatedAt: Timestamp.now()
      };

      if (editingAccount) {
        await updateDoc(doc(db, 'accounts', editingAccount.id), dataToSave);
        toast.success('Account updated successfully');
      } else {
        await addDoc(collection(db, 'accounts'), {
          ...dataToSave,
          createdAt: Timestamp.now()
        });
        toast.success('Account created successfully');
      }
      setIsModalOpen(false);
      setEditingAccount(null);
    } catch (error) {
      console.error('Save account error:', error);
      handleFirestoreError(error, editingAccount ? OperationType.UPDATE : OperationType.CREATE, 'accounts');
      toast.error('Failed to save account');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!isAdmin && !isManager && !isAccountant) return;
    setConfirmModal({
      isOpen: true,
      title: 'Delete Account',
      message: 'Are you sure you want to delete this account? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'accounts', id));
          toast.success('Account deleted');
        } catch (error) {
          toast.error('Failed to delete account');
        }
      }
    });
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      type: account.type,
      balance: account.balance,
      currency: account.currency,
      accountName: account.accountName || '',
      accountNumber: account.accountNumber || '',
      bankName: account.bankName || '',
      branchName: account.branchName || '',
      branchCode: account.branchCode || '',
      sortCode: account.sortCode || '',
      swiftCode: account.swiftCode || ''
    });
    setIsModalOpen(true);
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-black text-slate-900 flex items-center gap-3"><Wallet className="w-10 h-10 text-blue-800" /> Accounts</h1>
        {(isAdmin || isManager || isAccountant) && (
          <button 
            onClick={() => { 
              setIsModalOpen(true); 
              setEditingAccount(null); 
              setFormData({ 
                name: '', 
                type: 'bank', 
                balance: 0, 
                currency: 'TZS',
                accountName: '',
                accountNumber: '',
                bankName: '',
                branchName: '',
                branchCode: '',
                sortCode: '',
                swiftCode: ''
              }); 
            }} 
            className="bg-blue-800 text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2"
          >
            <Plus className="w-5 h-5" /> New Account
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {accounts.map(account => (
          <div key={account.id} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col justify-between">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase">{account.type}</p>
                <p className="text-xl font-black text-slate-900">{account.name}</p>
                {account.type === 'bank' && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-slate-500 font-medium">Bank: <span className="text-slate-900">{account.bankName}</span></p>
                    <p className="text-xs text-slate-500 font-medium">Branch: <span className="text-slate-900">{account.branchName} {account.branchCode ? `(${account.branchCode})` : ''}</span></p>
                    <p className="text-xs text-slate-500 font-medium">A/C: <span className="text-slate-900">{account.accountNumber}</span></p>
                  </div>
                )}
              </div>
              {(isAdmin || isManager || isAccountant) && (
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(account)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(account.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            <p className="text-2xl font-black text-blue-800">{account.currency} {account.balance.toLocaleString()}</p>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm overflow-y-auto">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white p-8 rounded-[2.5rem] w-full max-w-2xl relative my-auto">
              <button 
                onClick={() => setIsModalOpen(false)} 
                className="absolute top-6 right-6 p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400"
              >
                <X className="w-6 h-6" />
              </button>
              <h2 className="text-2xl font-black mb-6">{editingAccount ? 'Edit Account' : 'New Account'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase ml-1">Internal Reference Name</label>
                    <input required placeholder="e.g. Operation Bank Account" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-3 bg-slate-50 rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase ml-1">Account Type</label>
                    <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value as any})} className="w-full p-3 bg-slate-50 rounded-xl">
                      <option value="bank">Bank</option>
                      <option value="cash">Cash</option>
                      <option value="Mobile Money">Mobile Money</option>
                      <option value="Fixed Assets">Fixed Assets</option>
                      <option value="Current Liabilities">Current Liabilities</option>
                      <option value="Non-Current Liabilities">Non-Current Liabilities</option>
                      <option value="Equity">Equity</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase ml-1">Currency</label>
                    <input required placeholder="TZS, USD, etc." value={formData.currency} onChange={e => setFormData({...formData, currency: e.target.value})} className="w-full p-3 bg-slate-50 rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase ml-1">Initial Balance (Optional)</label>
                    <input type="number" step="0.01" placeholder="0.00" value={formData.balance} onChange={e => setFormData({...formData, balance: e.target.value === '' ? 0 : parseFloat(e.target.value)})} className="w-full p-3 bg-slate-50 rounded-xl" />
                  </div>
                </div>

                {formData.type === 'bank' && (
                  <div className="space-y-4 border-t border-slate-100 pt-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase ml-1">Select Bank</label>
                        <select 
                          required={formData.type === 'bank'}
                          value={formData.bankName} 
                          onChange={e => setFormData({...formData, bankName: e.target.value})} 
                          className="w-full p-3 bg-slate-50 rounded-xl"
                        >
                          <option value="">Select a Bank</option>
                          {TANZANIAN_BANKS.map(bank => (
                            <option key={bank} value={bank}>{bank}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase ml-1">Account Name</label>
                        <input required={formData.type === 'bank'} placeholder="Official Account Name" value={formData.accountName} onChange={e => setFormData({...formData, accountName: e.target.value})} className="w-full p-3 bg-slate-50 rounded-xl" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase ml-1">Account Number</label>
                        <input required={formData.type === 'bank'} placeholder="Account Number" value={formData.accountNumber} onChange={e => setFormData({...formData, accountNumber: e.target.value})} className="w-full p-3 bg-slate-50 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase ml-1">Branch Name</label>
                        <input placeholder="Branch Name" value={formData.branchName} onChange={e => setFormData({...formData, branchName: e.target.value})} className="w-full p-3 bg-slate-50 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase ml-1">Branch Code</label>
                        <input placeholder="Branch Code" value={formData.branchCode} onChange={e => setFormData({...formData, branchCode: e.target.value})} className="w-full p-3 bg-slate-50 rounded-xl" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase ml-1">Sort Code</label>
                        <input placeholder="Sort Code (Optional)" value={formData.sortCode} onChange={e => setFormData({...formData, sortCode: e.target.value})} className="w-full p-3 bg-slate-50 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-400 uppercase ml-1">SWIFT Code</label>
                        <input placeholder="SWIFT Code (Optional)" value={formData.swiftCode} onChange={e => setFormData({...formData, swiftCode: e.target.value})} className="w-full p-3 bg-slate-50 rounded-xl" />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setIsModalOpen(false)} disabled={isSubmitting} className="flex-1 bg-slate-100 text-slate-600 py-3 rounded-xl font-bold hover:bg-slate-200 transition-all font-mono uppercase text-xs tracking-widest disabled:opacity-50">Close</button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 bg-blue-800 text-white py-3 rounded-xl font-bold hover:bg-blue-900 transition-all font-mono uppercase text-xs tracking-widest shadow-lg shadow-blue-900/20 disabled:opacity-50 flex items-center justify-center gap-2">
                    {isSubmitting && <Loader2 className="w-3 h-3 animate-spin" />}
                    {editingAccount ? 'Update Account' : 'Save Account'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
        isDanger={true}
      />
    </div>
  );
};

export default AccountsPage;
